/* eslint-disable react/jsx-no-target-blank */
import { css, keyframes, useTheme } from '@emotion/react'
import { Typography, Link, Button } from '../base-ui'
import { BsFillArrowUpSquareFill } from 'react-icons/bs'
import Image from 'next/image'
import { useDataProvider } from '../../hooks/dataProvider'
import EditorTypography from './EditorTypography'
import { useTranslation } from 'next-i18next'

function Footer() {
    const { t } = useTranslation()
    const { data } = useDataProvider()
    const theme = useTheme()
    const pulse = keyframes`
        from {
            transform: scale3d(1, 1, 1);
        }

        50% {
            transform: scale3d(1.15, 1.15, 1.15);
        }

        to {
            transform: scale3d(1, 1, 1);
        }
`
    const goToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        })
    }

    return (
        <footer className="block w-full float-right relative">
            <div
                className="grid md:grid-cols-2 lg:grid-cols-5 pt-10 pb-20 lg:py-10 px-6 lg:px-20 2xl:px-52 relative"
                css={css`
                    background: url('/img/footer-background.png'),
                        ${theme.colors.text};
                    background-size: cover;
                    background-position: top;
                    color: ${theme.colors.white};
                `}
            >
                <div className="lg:col-span-3 lg:px-10">
                    <Image
                        alt="forexrebate logo"
                        src="/img/shared/logo.png"
                        placeholder="empty"
                        width={150}
                        height={64}
                        style={{ filter: 'brightness(0) invert(1)' }}
                    />
                    <EditorTypography
                        type="p"
                        fontSize="1rem"
                        color="white"
                        className="pt-4 pb-10"
                    >
                        <p
                            dangerouslySetInnerHTML={{
                                __html: data?.footerData?.sections?.[0]?.content
                            }}
                        />
                    </EditorTypography>
                </div>
                <div className="py-6 lg:pt-0 lg:px-4 md:mx-0 md:mr-10 lg:mr-0">
                    <Typography type="h6" color="white">
                        {t('quick access')}
                    </Typography>
                    <ul
                        className="list-disc list-inside pt-2 lg:pt-6"
                        css={css`
                            color: ${theme.colors.primary};
                        `}
                    >
                        <li className="py-2">
                            <Link color="white" href="/">
                                {t('home')}
                            </Link>
                        </li>
                        <li className="py-2">
                            <Link color="white" href="/brokers">
                                {t('brokers')}
                            </Link>
                        </li>
                        <li className="py-2">
                            <Link color="white" href="/charts">
                                {t('charts')}
                            </Link>
                        </li>
                        <li className="py-2">
                            <Link color="white" href="/blog">
                                {t('blog')}
                            </Link>
                        </li>
                        <li className="py-2">
                            <Link color="white" href="/about">
                                {t('about us')}
                            </Link>
                        </li>
                        <li className="py-2">
                            <Link color="white" href="/contact">
                                {t('contact us')}
                            </Link>
                        </li>
                    </ul>
                </div>
                <div className="lg:px-4 py-6 lg:pt-0 md:mr-10 lg:mr-0">
                    <Typography type="h6" color="white">
                        {t('contact us')}
                    </Typography>
                    <ul className="text-gray-silver pt-2 lg:pt-6">
                        <li className="py-2 flex items-center">
                            <Image
                                alt="socials"
                                src="/img/footer/socials/phone.svg"
                                width={32}
                                height={32}
                            />
                            <Link
                                href="tel:02188888889"
                                color="white"
                                external={true}
                            >
                                <span dir="ltr" className="mx-2">
                                    {data?.contactData?.sections?.[0]?.content}
                                </span>
                            </Link>
                        </li>
                        <li className="py-2 flex items-center">
                            <Image
                                alt="socials"
                                src="/img/footer/socials/mail.svg"
                                width={32}
                                height={32}
                            />
                            <Link
                                href="mailto:cryptoline@gmail.com"
                                color="white"
                                external={true}
                            >
                                <span
                                    dir="ltr"
                                    className="mx-2"
                                    css={css`
                                        font-size: ${theme.fontSize.sm};
                                    `}
                                >
                                    {data?.contactData?.sections?.[1]?.content}
                                </span>
                            </Link>
                        </li>
                        <li className="py-2">
                            <span dir="ltr" className="">
                                {data?.contactData?.sections?.[2]?.content}
                            </span>
                        </li>
                    </ul>
                    <div className="flex mt-6 w-36 mb-6 lg:mb-20 justify-around">
                        <Link
                            external={true}
                            href={data?.contactData?.sections?.[10]?.content}
                        >
                            <Image
                                alt="socials"
                                src="/img/footer/socials/telegram.svg"
                                width={32}
                                height={32}
                            />
                        </Link>
                        <Link
                            external={true}
                            href={data?.contactData?.sections?.[11]?.content}
                        >
                            <Image
                                alt="socials"
                                src="/img/footer/socials/instagram.svg"
                                width={32}
                                height={32}
                            />
                        </Link>
                        <Link
                            external={true}
                            href={data?.contactData?.sections?.[12]?.content}
                        >
                            <Image
                                alt="socials"
                                src="/img/footer/socials/youtube.svg"
                                width={32}
                                height={32}
                            />
                        </Link>
                        <Link
                            external={true}
                            href={data?.contactData?.sections?.[13]?.content}
                        >
                            <Image
                                alt="socials"
                                src="/img/footer/socials/twitter.svg"
                                width={32}
                                height={32}
                            />
                        </Link>
                    </div>
                </div>
                <div
                    className="text-center fixed bottom-10 z-10 right-4 lg:right-10"
                    css={css`
                        &:hover a {
                            animation: ${pulse} 1s ease-in-out infinite;
                        }
                    `}
                >
                    <Button
                        color="lightGray"
                        onClick={goToTop}
                        style="padding: 0; box-shadow: none; &:hover {box-shadow: none;}"
                    >
                        <BsFillArrowUpSquareFill
                            className="text-3xl"
                            css={css`
                                color: ${theme.colors.secondaryLight};
                            `}
                        />
                    </Button>
                </div>
                <div>v1.12.2</div>
            </div>
        </footer>
    )
}

export default Footer
